import { useEffect, useId, useRef, useState } from 'react';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { externalLocationApi } from '../../api/externalLocationApi';
import { EXTERNAL_SEARCH_DEBOUNCE_MS, EXTERNAL_SEARCH_MIN_QUERY_LENGTH } from './externalLocationProvider';

/**
 * 外部地點搜尋元件（MAP-GOOGLE-E1 / E2）。
 *
 * Props:
 *   searchProvider  — createExternalLocationSearchProvider() 回傳的 provider（預設 disabled）
 *   onResolved      — ({ locationId, displayName, ... }) => void；Activity write 只需 locationId
 *   disabled        — boolean
 *   debounceMs      — 輸入 debounce（預設 300ms）
 *   minQueryLength  — 最短 query（預設 2）
 *
 * Google script 僅由 searchProvider（mode=google）lazy load；disabled 時本元件不渲染、不發網路請求。
 */
export default function ExternalPlaceSearch({
  searchProvider,
  onResolved,
  disabled = false,
  debounceMs = EXTERNAL_SEARCH_DEBOUNCE_MS,
  minQueryLength = EXTERNAL_SEARCH_MIN_QUERY_LENGTH,
}) {
  const { t } = useTravelTranslation();
  const inputId = useId();
  const listboxId = useId();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchPhase, setSearchPhase] = useState('idle');
  const [resolvePhase, setResolvePhase] = useState('idle');
  const [error, setError] = useState('');
  const searchVersion = useRef(0);
  const abortRef = useRef(null);

  const providerEnabled = searchProvider?.enabled ?? false;
  const [providerReady, setProviderReady] = useState(!providerEnabled);

  useEffect(() => {
    const trimmed = query.trim();
    const handle = window.setTimeout(() => setDebouncedQuery(trimmed), debounceMs);
    return () => window.clearTimeout(handle);
  }, [query, debounceMs]);

  useEffect(() => {
    if (!providerEnabled) {
      setProviderReady(true);
      return undefined;
    }
    let cancelled = false;
    setProviderReady(false);
    const warmup = searchProvider?.warmup;
    if (typeof warmup !== 'function') {
      setProviderReady(true);
      return undefined;
    }
    warmup()
      .then(() => {
        if (!cancelled) setProviderReady(true);
      })
      .catch(() => {
        // init 失敗留給 search() 統一顯示 sanitized error；不在此搶先 alert
        if (!cancelled) setProviderReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [providerEnabled, searchProvider]);

  useEffect(() => {
    if (!providerEnabled || !providerReady || !debouncedQuery || debouncedQuery.length < minQueryLength) {
      setCandidates([]);
      setActiveIndex(-1);
      setSearchPhase('idle');
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      return undefined;
    }

    const version = ++searchVersion.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setSearchPhase('loading');
    setError('');

    searchProvider.search(debouncedQuery, { signal: controller.signal }).then((results) => {
      if (controller.signal.aborted || version !== searchVersion.current) return;
      setCandidates(results || []);
      setActiveIndex(-1);
      setSearchPhase('ready');
    }).catch((cause) => {
      if (controller.signal.aborted || version !== searchVersion.current) return;
      if (cause?.name === 'AbortError') return;
      setError(friendlySearchError(cause));
      setCandidates([]);
      setSearchPhase('error');
    });

    return () => {
      controller.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, providerEnabled, providerReady, minQueryLength]);

  async function selectCandidate(candidate) {
    if (disabled || resolvePhase === 'loading' || !candidate) return;
    setResolvePhase('loading');
    setError('');
    setCandidates([]);
    setActiveIndex(-1);
    // Autocomplete session 結束於選取；後續 Place Details 由 backend resolve
    if (typeof searchProvider?.retireSessionToken === 'function') {
      searchProvider.retireSessionToken();
    }
    try {
      const payload = await externalLocationApi.resolve({
        provider: candidate.provider,
        providerPlaceId: candidate.providerPlaceId,
      });
      const data = payload?.data?.data ?? payload?.data ?? payload;
      setResolvePhase('idle');
      setQuery('');
      setDebouncedQuery('');
      if (onResolved) {
        onResolved({
          locationId: data.locationId,
          displayName: data.displayName,
          canonicalName: data.canonicalName,
          address: data.address,
          latitude: data.latitude,
          longitude: data.longitude,
          locationType: data.locationType,
          provider: data.provider,
          providerPlaceId: data.providerPlaceId,
        });
      }
    } catch (cause) {
      setError(friendlyResolveError(cause));
      setResolvePhase('idle');
    }
  }

  function onKeyDown(event) {
    if (!candidates.length) {
      if (event.key === 'Escape') {
        setQuery('');
        setError('');
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((idx) => (idx + 1) % candidates.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((idx) => (idx <= 0 ? candidates.length - 1 : idx - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0 && activeIndex < candidates.length) {
        selectCandidate(candidates[activeIndex]);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setCandidates([]);
      setActiveIndex(-1);
      setError('');
    }
  }

  if (!providerEnabled) {
    return null;
  }

  const activeOptionId = activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined;

  return (
    <div className="external-place-search">
      <label htmlFor={inputId} className="external-place-search__label">
        {t('travel.places.searchLabel')}
      </label>
      <input
        id={inputId}
        className="external-place-search__input"
        type="text"
        role="combobox"
        aria-expanded={candidates.length > 0}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        value={query}
        placeholder={t('travel.places.searchPlaceholder')}
        disabled={disabled || resolvePhase === 'loading'}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {searchPhase === 'loading' && (
        <p aria-live="polite">
          {providerReady ? t('travel.places.searching') : t('travel.places.loadingProvider')}
        </p>
      )}
      {resolvePhase === 'loading' && <p aria-live="polite">{t('travel.places.resolving')}</p>}
      {error && <p role="alert" className="external-place-search__error">{error}</p>}
      {candidates.length > 0 && (
        <ul
          id={listboxId}
          className="external-place-search__candidates"
          role="listbox"
          aria-label={t('travel.places.candidatesLabel')}
        >
          {candidates.map((c, index) => (
            <li
              key={`${c.provider}:${c.providerPlaceId}`}
              id={`${listboxId}-opt-${index}`}
              role="option"
              aria-selected={index === activeIndex}
            >
              <button
                type="button"
                className={index === activeIndex ? 'is-active' : undefined}
                disabled={disabled || resolvePhase === 'loading'}
                onClick={() => selectCandidate(c)}
              >
                <strong>{c.primaryText}</strong>
                {c.secondaryText && <small>{c.secondaryText}</small>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* Google Places Autocomplete attribution（無內嵌地圖時仍需標示） */}
      <p className="external-place-search__attribution">
        Powered by <span lang="en">Google</span>
      </p>
    </div>
  );
}

function friendlySearchError(cause) {
  const message = String(cause?.message || '');
  if (/disabled|not configured|missing/i.test(message)) {
    return '外部地點搜尋目前無法使用。';
  }
  return '搜尋失敗，請稍後再試。';
}

function friendlyResolveError(cause) {
  const message = String(cause?.message || '');
  if (/disabled|unavailable|503/i.test(message)) {
    return '外部地點服務未啟用或暫時無法使用。';
  }
  return '解析地點失敗，請稍後再試。';
}
